// i18n
import Icon from 'presentations/views/components/atoms/Icon';
import Link from 'presentations/views/components/atoms/Link';
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import * as tracker from 'presentations/views/helpers/analytics';
import { useRouter } from 'next/router';

import styled from '@emotion/styled';
import { css } from '@emotion/react';

import {
  fontSize,
  fontWeight,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import Dialog from 'presentations/views/components/atoms/Dialog';
import Button from 'presentations/views/components/atoms/Button';
import { pricingPath } from 'presentations/routes/path';
import { useState } from 'react';
import { black } from 'presentations/views/styles/color';
import { EventTypeListItem } from 'presentations/api/schema/event_type';
import { isiOSApp as isiOSAppFunc } from 'presentations/views/helpers/electron';
import { ViewerItem } from 'presentations/api/schema/user';
import { PlanId } from 'domains/models/subscription/planId';

interface Props {
  viewer: ViewerItem;
  eventType: EventTypeListItem;
  onClose: () => void;
  onSubmit: any;
}

export default function Component(props: Props) {
  const router = useRouter();
  const isiOSApp = isiOSAppFunc();
  const [isConfirm, setIsConfirm] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const t = translateInComponent(translations);

  const isPro = props.viewer.planId === PlanId.Pro;

  const onClickActivate = () => {
    setIsConfirm(true);
  };

  const onClickUpgrade = () => {
    tracker.setEvent('click upgrade');
    if (isiOSApp) {
      window.open(pricingPath());
    } else {
      router.push(pricingPath());
    }
  };

  const onSubmit = async () => {
    tracker.setEvent('activate eventType as free plan', {
      eventTypeId: props.eventType.id,
    });
    setIsLoading(true);
    await props.onSubmit(props.eventType);
    setIsLoading(false);
    props.onClose();
  };

  return (
    <Dialog
      isOpen
      onClose={props.onClose}
      title={
        isConfirm
          ? t(`title confirm${isPro ? ' for startup' : ''}`)
          : t('title')
      }
      disableMinWidth
    >
      {isConfirm ? (
        <Container>
          <Message isConfirm={isConfirm}>
            {t(`message confirm${isPro ? ' for startup' : ''}`)}
          </Message>
          <ConfirmButtonContainer>
            <CancelButton onClick={props.onClose} size={'large'}>
              {t('cancel button')}
            </CancelButton>
            <SubmitButton
              size={'large'}
              type="submit"
              color="primary"
              onClick={onSubmit}
              isLoading={isLoading}
            >
              {t('submit button')}
            </SubmitButton>
          </ConfirmButtonContainer>
        </Container>
      ) : (
        <Container>
          <Message isConfirm={isConfirm}>
            {t(`message${isPro ? ' for startup' : ''}`)}
          </Message>
          <ButtonContainer>
            <UpgradeButton
              color="primary"
              size="large"
              onClick={onClickUpgrade}
            >
              {t('upgrade plan button')}
            </UpgradeButton>
            <ActivateLink onClick={onClickActivate}>
              <ActivateArrowIcon name="chevron-right" color="primary" />
              <ActivateLabel>
                {t(
                  `disable paid features button${isPro ? ' for startup' : ''}`,
                )}
              </ActivateLabel>
            </ActivateLink>
          </ButtonContainer>
        </Container>
      )}
    </Dialog>
  );
}

const Container = styled.div`
  text-align: center;
`;

const Message = styled.div<{
  isConfirm: boolean;
}>`
  margin-bottom: ${spacing(24)};
  white-space: pre-wrap;
  text-align: left;
  ${(props) => css`
    max-width: ${props.isConfirm ? '470' : '360'}px;
  `};

  ${mediaMax.tablet} {
    margin-bottom: ${spacing(12)};
    font-size: ${fontSize(11)};
  }
`;
const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const UpgradeButton = styled(Button)`
  width: 201px;
`;
const ConfirmButtonContainer = styled.div`
  margin: 0 auto;
  display: flex;
  justify-content: center;
`;
const SubmitButton = styled(Button)`
  width: 168px;
  ${mediaMax.smartphone} {
    width: 128px;
  }
`;
const CancelButton = styled(Button)`
  width: 168px;
  margin-right: ${spacing(20)};
  ${mediaMax.smartphone} {
    width: 128px;
  }
`;
const ActivateLink = styled(Link)`
  display: flex;
  align-items: center;
  margin-top: ${spacing(16)};
  margin-bottom: ${spacing(20)};
  font-weight: ${fontWeight('bold')};
`;
const ActivateArrowIcon = styled(Icon)`
  width: 14px !important;
  height: 14px !important;
`;
const ActivateLabel = styled.span`
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('normal')};
  color: ${black};
`;
